import { FONTS, GAPS, SIZES } from '../constants';
import { ComputedNotifications, NotificationConfig, NotificationLayout } from '../types';

/** Кэш измерений текста */
const textMeasureCache = new Map<string, number>();

/** Кэш лейаута */
let layoutCache: {
    key: string;
    layout: NotificationLayout;
} | null = null;

// Сброс кэша после загрузки шрифтов
if (typeof document !== 'undefined' && document.fonts) {
    document.fonts.ready.then(() => {
        layoutCache = null;
        textMeasureCache.clear();
    });
}

/**
 * Измеряет ширину текста с кэшированием.
 */
function measureText(ctx: CanvasRenderingContext2D, text: string, font: string): number {
    const cacheKey = `${font}|${text}`;

    const cachedWidth = textMeasureCache.get(cacheKey);
    if (cachedWidth !== undefined) {
        return cachedWidth;
    }

    ctx.save();
    ctx.font = font;
    const width = ctx.measureText(text).width;
    ctx.restore();

    textMeasureCache.set(cacheKey, width);
    return width;
}

/**
 * Рассчитывает ширину одной нотификации.
 */
function measureNotificationWidth(ctx: CanvasRenderingContext2D, config: NotificationConfig): number {
    let width = SIZES.containerPaddingX * 2; // padding слева и справа

    // Иконки
    const iconsWidth = config.icons.length * SIZES.icon + (config.icons.length - 1) * SIZES.iconGap;
    width += iconsWidth + GAPS.afterIcons;

    // Текст
    const textWidth = measureText(ctx, config.text, FONTS.text);
    width += textWidth;

    // Действия (если есть)
    if (config.actions && config.actions.length > 0) {
        width += GAPS.afterText;

        for (let i = 0; i < config.actions.length; i++) {
            const action = config.actions[i];

            // Разделитель перед действием (кроме первого)
            if (i > 0) {
                width += SIZES.separatorMarginX * 2 + SIZES.separatorWidth;
            }

            // Текст действия
            const labelWidth = measureText(ctx, action.label, FONTS.text);
            width += labelWidth + GAPS.afterActionLabel;

            // DTMF-команды
            const commandsWidth =
                action.command.length * SIZES.dtmfCircle + (action.command.length - 1) * SIZES.dtmfCircleGap;
            width += commandsWidth;
        }
    }

    return width;
}

/**
 * Рассчитывает ширину объединённой secondary-нотификации.
 */
function measureCombinedWidth(ctx: CanvasRenderingContext2D, configs: NotificationConfig[]): number {
    if (configs.length === 0) return 0;

    let width = SIZES.containerPaddingX * 2;

    for (let i = 0; i < configs.length; i++) {
        const config = configs[i];

        // Разделитель перед элементом (кроме первого)
        if (i > 0) {
            width += SIZES.separatorMarginX * 2 + SIZES.separatorWidth;
        }

        // Иконки
        const iconsWidth = config.icons.length * SIZES.icon + (config.icons.length - 1) * SIZES.iconGap;
        width += iconsWidth + GAPS.afterIcons;

        // Текст
        const textWidth = measureText(ctx, config.text, FONTS.text);
        width += textWidth;
    }

    return width;
}

/**
 * Генерирует ключ кэша для лейаута.
 *
 * Ключ зависит только от размеров canvas и набора id нотификаций.
 * Тексты нотификаций статичны и не зависят от локали в runtime.
 * При изменении текстов или добавлении динамических строк — пересмотреть стратегию кэширования.
 */
function generateCacheKey(
    canvasWidth: number,
    canvasHeight: number,
    notifications: ComputedNotifications,
    shouldDrawAloneScreen: boolean
): string {
    const primaryIds = notifications.primary
        .map((n) => n.id)
        .sort()
        .join(',');
    const secondaryIds = notifications.secondary
        .map((n) => n.id)
        .sort()
        .join(',');
    return (
        `${canvasWidth}x${canvasHeight}|${primaryIds}|${secondaryIds}` +
        `${shouldDrawAloneScreen ? '|shouldDrawAloneScreen' : ''}`
    );
}

/**
 * Рассчитывает позиции и размеры всех нотификаций.
 * Результат кэшируется до изменения входных параметров.
 */
export function calculateLayout(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    notifications: ComputedNotifications,
    shouldDrawAloneScreen: boolean
): NotificationLayout {
    // Проверяем кэш
    const cacheKey = generateCacheKey(canvasWidth, canvasHeight, notifications, shouldDrawAloneScreen);
    if (layoutCache && layoutCache.key === cacheKey) {
        return layoutCache.layout;
    }

    const layout: NotificationLayout = {
        primaryAreas: [],
        secondaryArea: null,
    };
    const paddingBottom = shouldDrawAloneScreen ? SIZES.paddingBottomAloneScreen : SIZES.paddingBottom;
    // === Primary row (отдельные нотификации) ===
    if (notifications.primary.length > 0) {
        // Измеряем ширины всех нотификаций
        const widths = notifications.primary.map((config) => measureNotificationWidth(ctx, config));

        // Общая ширина с gap'ами
        const totalWidth = widths.reduce((sum, w) => sum + w, 0) + (widths.length - 1) * SIZES.notificationGap;

        // Y-координата (от низа) — primary row всегда на фиксированной позиции
        const bottomOffset = paddingBottom + SIZES.containerHeight + SIZES.rowGap;
        const y = canvasHeight - bottomOffset - SIZES.containerHeight;

        // X начинается от центра
        let x = (canvasWidth - totalWidth) / 2;

        for (let i = 0; i < notifications.primary.length; i++) {
            layout.primaryAreas.push({
                x,
                y,
                width: widths[i],
                height: SIZES.containerHeight,
            });
            x += widths[i] + SIZES.notificationGap;
        }
    }

    // === Secondary row (объединённая нотификация) ===
    if (notifications.secondary.length > 0) {
        const width = measureCombinedWidth(ctx, notifications.secondary);
        const x = (canvasWidth - width) / 2;
        const y = canvasHeight - paddingBottom - SIZES.containerHeight;

        layout.secondaryArea = {
            x,
            y,
            width,
            height: SIZES.containerHeight,
        };
    }

    // Сохраняем в кэш
    layoutCache = { key: cacheKey, layout };

    return layout;
}
